import { type ReactNode, useCallback, useState } from 'react';

import { CORE_PAGES } from '@core-ui/components';
import { SkeletonLoader } from '@moniebook-web/components/atoms/skeleton-loader';
import {
    Body,
    BodyLarge,
    BodySmall,
} from '@moniebook-web/components/atoms/typography';
import { FeatureProtectedView } from '@moniebook-web/components/molecules/feature-protected-view';
import {
    DropdownWidget,
    useDropdownWidgetContext,
} from '@moniebook-web/components/widgets/dropdown';
import { ArrowDownSFill } from '@moniebook-web/icons/arrows/arrow-down-s-fill';
import { ArrowRightLine } from '@moniebook-web/icons/arrows/arrow-right-line';
import { ArrowRightSLine } from '@moniebook-web/icons/arrows/arrow-right-s-line';
import { Building4Fill } from '@moniebook-web/icons/buildings/building-4-fill';
import { CustomerService2Fill } from '@moniebook-web/icons/business/customer-service-2-fill';
import { FileTextFill } from '@moniebook-web/icons/document/file-text-fill';
import { AddLine } from '@moniebook-web/icons/system/add-line';
import { CheckLine } from '@moniebook-web/icons/system/check-line';
import { Settings3Fill } from '@moniebook-web/icons/system/settings-3-fill';
import { PAGES } from '@moniebook-web/types';
import {
    baseConfig,
    logout,
    useCustomerAnalytics,
    useSubscriptionData,
    useUserInfo,
} from '@moniebook-web/utils';
import { useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { Link, type LinkProps, useNavigate } from 'react-router';

import { useSessionTokenPayload } from '@/features/ghost-session/context/session-token-payload-context';
import { ANALYTIC_EVENTS, BRAZE_EVENTS } from '@/shared/constants';

import { BusinessSwitcher } from './business-switcher';

function Title({
    children = '',
    loading = false,
}: Readonly<{ loading?: boolean; children?: string }>) {
    return (
        <div className='flex size-20 w-full items-center gap-8'>
            <div className='flex size-20 items-center justify-center rounded-full bg-[black] font-medium text-12 text-white leading-10'>
                {children?.charAt(0).toUpperCase()}
            </div>
            <div className='flex w-[81%] flex-1 flex-col pr-4 text-left'>
                {loading ? (
                    <SkeletonLoader width='100%' />
                ) : (
                    <span className='block min-w-0 flex-1 truncate text-14 text-content-body-default'>
                        {children}
                    </span>
                )}
            </div>
        </div>
    );
}

export function UserDropdown() {
    const [openSwitchBusiness, setOpenSwitchBusiness] = useState(false);
    const { data } = useSubscriptionData();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { sendBrazeEvent, sendMixpanelEvent } = useCustomerAnalytics();
    const { currentBusiness, canCreateBusiness, loading } = useUserInfo(true);
    const { isGhostSession } = useSessionTokenPayload();

    const handleToggleShow = () => {
        setOpenSwitchBusiness((currentSTate) => !currentSTate);
    };

    const handleSendEvents = useCallback(() => {
        sendMixpanelEvent({
            eventName: ANALYTIC_EVENTS.VIEW_CREATE_BUSINESS_FORM,
        });

        sendBrazeEvent({
            eventName: BRAZE_EVENTS.CREATE_BUSINESS_CLICKED,
        });
    }, [sendBrazeEvent, sendMixpanelEvent]);

    return (
        <>
            <BusinessSwitcher
                open={openSwitchBusiness}
                onClose={() => handleToggleShow()}
            />
            <DropdownWidget
                title={<Title loading={loading}>{currentBusiness?.name}</Title>}
                chevron={
                    <ArrowDownSFill className='text-content-label-default' />
                }>
                <section className='flex flex-col border-ui-border-default border-b-[0.5px] p-8'>
                    <DropdownItem
                        to=''
                        leftSlot={
                            <div className='flex size-32 shrink-0 items-center justify-center rounded-full bg-content-heading-default'>
                                <BodyLarge className='font-medium text-white'>
                                    {currentBusiness?.name
                                        .charAt(0)
                                        ?.toUpperCase()}
                                </BodyLarge>
                            </div>
                        }
                        text={{
                            title: currentBusiness?.name,
                            subtitle: currentBusiness?.roleName,
                        }}
                        rightSlot={
                            <CheckLine className='text-mb-accent-blue-400' />
                        }
                        className='h-56! bg-mb-accent-blue-300'
                    />
                </section>
                <section className='flex flex-col border-ui-border-default border-b-[0.5px] p-8'>
                    {!isGhostSession && (
                        <DropdownItem
                            to=''
                            leftSlot={
                                <Building4Fill className='size-20 text-content-label-alternative' />
                            }
                            text={{ title: 'Switch business' }}
                            onClick={() => {
                                sendMixpanelEvent({
                                    eventName: 'click_switch_business',
                                });
                                handleToggleShow();
                            }}
                            rightSlot={
                                <ArrowRightSLine className='text-content-label-default' />
                            }
                        />
                    )}
                    <FeatureProtectedView
                        flag='moniebook.pos.enableNewBusinessCreation'
                        noAltView>
                        {canCreateBusiness && (
                            <DropdownItem
                                to={CORE_PAGES.ONBOARDING.BUSINESS_DETAILS}
                                leftSlot={
                                    <AddLine className='size-20 text-content-label-alternative' />
                                }
                                text={{ title: 'Add a new business' }}
                                onClick={handleSendEvents}
                            />
                        )}
                    </FeatureProtectedView>
                    <DropdownItem
                        to={PAGES.SETTINGS}
                        leftSlot={
                            <Settings3Fill className='size-20 text-content-label-alternative' />
                        }
                        text={{ title: 'Settings' }}
                    />
                    <DropdownItem
                        text={{ title: 'Support' }}
                        leftSlot={
                            <CustomerService2Fill className='size-20 text-content-label-alternative' />
                        }
                        to={baseConfig.MONIEBOOK_CUSTOMER_HELP_URL}
                        target='_black'
                    />
                    <DropdownItem
                        text={{ title: 'Terms & conditions' }}
                        leftSlot={
                            <FileTextFill className='size-20 text-content-label-alternative' />
                        }
                        to={PAGES.TERMS}
                    />
                    <DropdownItem
                        text={{ title: 'Privacy policy' }}
                        leftSlot={
                            <FileTextFill className='size-20 text-content-label-alternative' />
                        }
                        to={PAGES.PRIVACY_POLICY}
                    />
                </section>
                {data?.planName?.toLowerCase().startsWith('free') && (
                    <section className='flex flex-col border-ui-border-default border-b-[0.5px] bg-mb-accent-sky-100 p-8'>
                        <div className='flex h-40 w-full items-center justify-between'>
                            <div className='flex flex-col gap-10'>
                                <Body className='text-mb-accent-lime-108'>
                                    Free plan
                                </Body>
                                <Body className='text-content-subtext-alternative'>
                                    Upgrade your plan
                                </Body>
                            </div>
                            <Link
                                to={CORE_PAGES.SETTINGS.SUBSCRIPTIONS}
                                className='rounded-48 bg-mb-accent-sky-104 p-8'>
                                <BodySmall
                                    className='text-white!'
                                    weight='semibold'>
                                    Upgrade
                                </BodySmall>
                            </Link>
                        </div>
                    </section>
                )}
                <section className='flex flex-col border-ui-border-default border-b-[0.5px] p-8'>
                    <DropdownItem
                        text={{ title: 'Sign out' }}
                        onClick={logout(navigate, queryClient)}
                        to={PAGES.LOGOUT}
                        leftSlot={
                            <ArrowRightLine className='size-20 text-content-label-alternative' />
                        }
                    />
                </section>
            </DropdownWidget>
        </>
    );
}

interface DropdownItemProps {
    leftSlot: ReactNode;
    text: {
        title?: string;
        subtitle?: string;
    };
    rightSlot?: ReactNode;
}

function DropdownItem({
    leftSlot,
    text,
    rightSlot,
    onClick,
    ...linkProps
}: Readonly<DropdownItemProps & LinkProps>) {
    const { handleToggle } = useDropdownWidgetContext();

    return (
        <Link
            {...linkProps}
            className={clsx(
                'flex h-40 items-center gap-12 rounded-[10px] px-8 transition-all hover:bg-mb-accent-blue-300',
                linkProps.className,
            )}
            onClick={(event) => {
                event.stopPropagation();
                if (!linkProps.to) event.preventDefault();
                onClick?.(event);
                handleToggle();
            }}>
            {leftSlot}
            <div className='flex flex-col truncate'>
                <Body className='overflow-hidden text-ellipsis leading-body'>
                    {text.title}
                </Body>
                {text.subtitle && (
                    <Body className='overflow-hidden text-ellipsis text-content-label-alternative leading-body'>
                        {text.subtitle}
                    </Body>
                )}
            </div>
            {!!rightSlot && <div className='ml-auto shrink-0'>{rightSlot}</div>}
        </Link>
    );
}
